import React, { useState, useId } from 'react';
import { AuditMessage } from '../../types';
import { auditService } from '../../services/auditService';
import { Search, Hash, User, AlertTriangle, Trash2 } from 'lucide-react';
import { iconForFileName } from '../../utils/fileIcon';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

type AuditMode = 'channel' | 'user';

export const MessageAudit: React.FC = () => {
  const fid = useId();
  const [mode, setMode] = useState<AuditMode>('channel');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [messages, setMessages] = useState<AuditMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [channelInfo, setChannelInfo] = useState<{ name: string; type: string } | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetId.trim() || !reason.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      if (mode === 'channel') {
        const result = await auditService.getChannelMessages(
          parseInt(targetId),
          reason.trim()
        );
        setMessages(result.messages);
        setTotal(result.total);
        setChannelInfo(result.channel);
      } else {
        const result = await auditService.getUserMessages(
          targetId.trim(),
          reason.trim()
        );
        setMessages(result.messages);
        setTotal(result.total);
        setChannelInfo(null);
      }
    } catch (err: any) {
      setError(err.message || '감사 조회 실패');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* Mode Selector */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => { setMode('channel'); setMessages([]); setSearched(false); }}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
            mode === 'channel' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Hash size={14} />
          채널 감사
        </button>
        <button
          onClick={() => { setMode('user'); setMessages([]); setSearched(false); }}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
            mode === 'user' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <User size={14} />
          사용자 감사
        </button>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div className="space-y-3">
          <div>
            <label htmlFor={`${fid}-target`} className="block text-xs text-slate-500 mb-1">
              {mode === 'channel' ? '채널 ID *' : '사용자 사번 *'}
            </label>
            <input id={`${fid}-target`}
              type="text"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder={mode === 'channel' ? '채널 ID를 입력하세요' : '사번을 입력하세요'}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor={`${fid}-reason`} className="block text-xs text-slate-500 mb-1">감사 사유 *</label>
            <input id={`${fid}-reason`}
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="감사 사유를 입력하세요 (필수)"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-500" />
            <span className="text-xs text-amber-600">
              조회 내역은 사유와 함께 감사 로그에 기록되고 최고 관리자에게 통지됩니다.
            </span>
          </div>
          <button
            type="submit"
            disabled={loading || !targetId.trim() || !reason.trim()}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            <Search size={14} />
            {loading ? '조회 중...' : '감사 조회'}
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-4 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded-lg border border-brand-200">
          {error}
        </div>
      )}

      {/* Results */}
      {searched && !loading && (
        // min-h-0 flex-col: 결과 목록이 조회 폼 아래 남는 높이까지만 차지하게 한다
        <div className="min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-500">
              {channelInfo && (
                <span className="font-medium text-slate-700">#{channelInfo.name} </span>
              )}
              총 {total}건의 메시지
            </span>
          </div>

          {messages.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              조회된 메시지가 없습니다.
            </div>
          ) : (
            <div className={`space-y-1.5 ${ADMIN_LIST_FILL}`}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg border text-sm ${
                    msg.deletedAt
                      ? 'bg-brand-50 border-brand-200'
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{msg.senderName}</span>
                      <span className="text-xs text-slate-400">{msg.senderId}</span>
                      {msg.channelName && (
                        <span className="text-xs text-brand-600">#{msg.channelName}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {msg.deletedAt && (
                        <span className="flex items-center gap-1 text-xs text-brand-600">
                          <Trash2 size={12} />
                          삭제됨
                        </span>
                      )}
                      {msg.attachment && (() => {
                        const TypeIcon = iconForFileName(msg.attachment.fileName);
                        return (
                          <span className="flex items-center gap-1 text-xs text-brand-600">
                            <TypeIcon size={12} />
                            {msg.attachment.fileName}
                          </span>
                        );
                      })()}
                      <span className="text-xs text-slate-400">{formatDate(msg.createdAt)}</span>
                    </div>
                  </div>
                  <div className="text-slate-700 whitespace-pre-wrap break-anywhere">
                    {msg.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
